import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ValidationErrors } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { take } from 'rxjs';

import { AuthService } from '@core/services/auth.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthDefaultValues } from '@state/auth/auth.selectors';
import { setAuthDefaultValues, setAuthModal } from '@state/auth/auth.actions';

@UntilDestroy()
@Component({
  selector: 'app-signin-buttons',
  templateUrl: './buttons.component.html',
  styleUrls: ['./buttons.component.scss'],
})
export class SignInButtonsComponent implements OnInit {
  protected userEmail = '';
  protected emailFocused = false;
  protected emailErrors?: ValidationErrors;
  protected emailDeactivated = false;
  protected emailDoesNotExist = false;
  protected loadingEmail = false;

  protected userPhoneNumber = '';
  protected phoneNumberFocused = false;
  protected phoneNumberError?: ValidationErrors;
  protected phoneNumberDeactivated = false;
  protected phoneNumberDoesNotExist = false;
  protected loadingPhoneNumber = false;

  protected isMobile = false;
  protected requestSent: 'email' | 'sms' | null = null;

  constructor(
    private authService: AuthService,
    private store: Store<AppState>,
    private deviceService: DeviceDetectorService
  ) {}

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();

    this.store
      .select(selectAuthDefaultValues)
      .pipe(take(1))
      .subscribe((values) => {
        this.userEmail = values?.email || '';
        this.userPhoneNumber = values?.phoneNumber || '';

        this.store.dispatch(
          setAuthDefaultValues({
            email: undefined,
            phoneNumber: undefined,
          })
        );
      });
  }

  validateEmail() {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(this.userEmail)) {
      this.emailErrors = {
        patternError: 'Enter a valid email address',
      };
      return false;
    }
    this.emailErrors = undefined;
    return true;
  }

  signInWithEmail() {
    if (!this.validateEmail()) {
      return;
    }

    this.loadingEmail = true;
    this.emailErrors = undefined;
    this.emailDoesNotExist = false;
    this.emailDeactivated = false;

    this.authService
      .requestEmailMagicLink({ email: this.userEmail })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.loadingEmail = false;
          this.emailFocused = false;
          if (res) {
            this.requestSent = 'email';
          } else {
            this.emailErrors = {
              patternError: 'Email not found or invalid. Please try again.',
            };
          }
        },
        error: (error) => {
          this.loadingEmail = false;

          if (error?.message?.includes('User not active')) {
            this.emailDeactivated = true;
          } else if (error?.message?.includes('User not found')) {
            this.emailDoesNotExist = true;
          } else {
            this.emailErrors = {
              patternError: 'Email not found or invalid. Please try again.',
            };
          }
        },
      });
  }

  validatePhoneNumber() {
    if (!this.userPhoneNumber || this.userPhoneNumber.length < 10) {
      this.phoneNumberError = {
        patternError: 'Enter a valid phone number',
      };
      return false;
    }
    this.phoneNumberError = undefined;
    return true;
  }

  signInWithPhoneNumber() {
    if (!this.validatePhoneNumber()) {
      return;
    }

    this.loadingPhoneNumber = true;
    this.phoneNumberError = undefined;
    this.phoneNumberDoesNotExist = false;
    this.phoneNumberDeactivated = false;

    this.authService
      .requestSmsMagicLink({ phoneNumber: this.userPhoneNumber })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.loadingPhoneNumber = false;
          this.phoneNumberFocused = false;
          if (res) {
            this.requestSent = 'sms';
          } else {
            this.phoneNumberError = {
              patternError:
                'Phone number not found or invalid. Please try again.',
            };
          }
        },
        error: (error) => {
          this.loadingPhoneNumber = false;

          if (error?.message?.includes('User not active')) {
            this.phoneNumberDeactivated = true;
          } else if (error?.message?.includes('User not found')) {
            this.phoneNumberDoesNotExist = true;
          } else {
            this.phoneNumberError = {
              patternError:
                'Phone number not found or invalid. Please try again.',
            };
          }
        },
      });
  }

  refreshPage() {
    window.location.reload();
  }

  showSignInModal(type: 'email' | 'phone-number'): void {
    this.store.dispatch(
      setAuthModal({
        authModal: 'signup',
        defaultEmail: type === 'email' ? this.userEmail : undefined,
        defaultPhoneNumber:
          type === 'phone-number' ? this.userPhoneNumber : undefined,
      })
    );
  }
}
